import React, { ChangeEvent, useEffect, useState, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Brand from '../../../assets/svg/brand.svg?react';
import BrandName from '../../../assets/svg/brand-name.svg?react';
import { ValidatorForm, TextValidator } from '../../common/form-validator';
import Button from '@mui/material/Button';
import { ROUTES } from '../../common/constants/routes';
import { sanitizeRedirectUrl } from '../../common/helpers/sanitize';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NWClient } from '../../client/NWClient';
import { toast } from 'react-toastify';
import { Box, FormControl, FormHelperText } from '@mui/material';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { Company } from '../../models/company.model';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectCountriesInfo } from '../../slices/countries/countriesSlice';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import { useErrorMessage } from '../../common/hooks/useErrorMessage';
import WhatsAppOptInCheckbox from '../../common/whatsapp-opt-in-checkbox/WhatsAppOptInCheckbox';
import PhoneNumberInput from '../../common/form-inputs/phone-number-input/PhoneNumberInput';

export const CompanyCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const onlyDigitSpaceRegExp = useMemo(() => {
    return /^[0-9 ]*$/;
  }, []);
  // Optional leading '+', then any number of digits (no length limit).
  const phoneRegExp = useMemo(() => {
    return /^\+?\d*$/;
  }, []);
  const businessNameRegEx = useMemo(() => {
    return /^[a-zA-Z0-9 &\-]*$/;
  }, []);
  const [searchParams] = useSearchParams();
  const fromPricingPage = searchParams.get('fromPricingPage');
  const redirectToStripe = searchParams.get('redirectToStripe');
  const productId = searchParams.get('productId');
  const priceId = searchParams.get('priceId');
  const backUrl = searchParams.get('backUrl');
  const urlParams = `?productId=${productId}&priceId=${priceId}`;
  const [businessName, setBusinessName] = useState('');
  const [businessPhoneNumber, setBusinessPhoneNumber] = useState('');
  const [businessABNorACN, setBusinessABNorACN] = useState('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [fieldsErrors, setFieldsErrors] = useState<any>(null);
  const [country, setCountry] = useState<{ value: string; title: string } | null>(null);
  const [whatsappOptIn, setWhatsappOptIn] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const countries = useAppSelector(selectCountriesInfo);
  const countriesOptions = [...countries]
    .filter((el) => el.code !== 'GB')
    .map((el) => ({ value: el.name, title: el.name }));

  const handleInputsPossibleErrors = (propertyName: string) => {
    if (fieldsErrors && fieldsErrors[propertyName]) {
      const obj = { ...fieldsErrors };
      delete obj[propertyName];
      setFieldsErrors(obj);
    }
  };

  const handleBusinessABNorACNChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBusinessABNorACN(e.target.value);
    handleInputsPossibleErrors('business_abn_acn');
  };

  const handleBusinessNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBusinessName(e.target.value);
    handleInputsPossibleErrors('name');
  };

  const basicContainerStyles = { margin: '0 auto' };

  const canSubmit =
    Boolean(businessName) &&
    (Boolean(country) || (!country && countriesOptions.length === 0)) &&
    Boolean(businessPhoneNumber);

  useEffect(() => {
    ValidatorForm.addValidationRule('ABNorACNLength', (value) => {
      return onlyDigitSpaceRegExp.test(value as string);
    });
    return () => {
      ValidatorForm.removeValidationRule('ABNorACNLength');
    };
  }, [onlyDigitSpaceRegExp]);

  useEffect(() => {
    ValidatorForm.addValidationRule('phone', (value) => {
      // Accepts any number of digits with an optional leading '+'.
      // Spaces are tolerated while typing because they are stripped on submit.
      const normalized = String(value ?? '')
        .trim()
        .replace(/\s/g, '');
      return normalized === '' || phoneRegExp.test(normalized);
    });

    return () => {
      ValidatorForm.removeValidationRule('phone');
    };
  }, [phoneRegExp]);

  useEffect(() => {
    ValidatorForm.addValidationRule('valueLength255', (value) => {
      return (value as string).trim().length < 256;
    });

    return () => {
      ValidatorForm.removeValidationRule('valueLength255');
    };
  }, []);

  useEffect(() => {
    ValidatorForm.addValidationRule('businessNameRule', (value) => {
      return businessNameRegEx.test((value as string).trim());
    });

    return () => {
      ValidatorForm.removeValidationRule('businessNameRule');
    };
  }, [businessNameRegEx]);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();

    const parsed = parsePhoneNumberFromString(businessPhoneNumber);

    if (!parsed?.isValid()) {
      setPhoneError(t('messages.phoneInvalid'));
      return;
    }

    if (whatsappOptIn) {
      const phoneType = parsed.getType();
      if (phoneType !== 'MOBILE' && phoneType !== 'FIXED_LINE_OR_MOBILE') {
        setPhoneError(t('messages.phoneNotWhatsApp'));
        return;
      }
    }

    setSubmitting(true);
    const data = {
      name: businessName.trim(),
      phone_number: businessPhoneNumber.trim().replace(/\s/g, ''),
      business_abn_acn: businessABNorACN.trim().replace(/\s/g, ''),
      country: country ? country.value : null,
      postal_country: country ? country.value : null,
      physical_country: country ? country.value : null,
    };

    NWClient.post('company', data)
      .then((res) => {
        const id = Number((res as Company).id);
        return NWClient.post(
          `business-profile-details/${id}`,
          {
            company: id,
            business_phone_number: businessPhoneNumber.trim().replace(/\s/g, ''),
            business_country: country ? country.value : null,
            registration_number: businessABNorACN.trim().replace(/\s/g, ''),
            marketing_opt_in: whatsappOptIn,
          },
          false
        )
          .then(() =>
            NWClient.post(
              `business-profile-bank-details/${id}`,
              { company: id, currency: 'AUD' },
              false
            )
          )
          .then(() => {
            toast.success(t('messages.companyCreated'));
            if (fromPricingPage === 'true') {
              navigate(ROUTES.PRODUCT_PLAN_CONFIRMATION + urlParams);
            } else if (backUrl) {
              navigate(sanitizeRedirectUrl(backUrl));
            } else if (redirectToStripe === 'true') {
              navigate(ROUTES.PRICING);
            } else {
              navigate(ROUTES.INDEX);
            }
            window.location.reload();
          });
      })
      .catch((error) => {
        setFieldsErrors(error.response?.data ?? null);
        toast.error(getErrorMessage(error));
        setSubmitting(false);
      });
  };

  return (
    <>
      <div className='company-create-page'>
        <Grid component='div' container style={{ maxWidth: 'xl', ...basicContainerStyles }}>
          <header>
            <Grid component='div' size={{ xs: 12 }}>
              <div className='logo-container' role='img' aria-label='Logo'>
                <Brand />
                <BrandName />
              </div>
            </Grid>
          </header>
        </Grid>
        <div className='company-create-container'>
          <Box component='h1' className='text-transform-none' sx={{ textAlign: 'center' }}>
            {t('messages.enterBusinessDetails')}
          </Box>
          <ValidatorForm onSubmit={handleSubmit} className='company-data-form'>
            <div>
              <TextValidator
                label={t('labels.businessOrCompany')}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleBusinessNameChange(e)}
                name='businessName'
                value={businessName}
                required={true}
                validators={['required', 'valueLength255', 'businessNameRule']}
                errorMessages={[
                  t('messages.fieldRequired'),
                  t('messages.maxLength255'),
                  t('messages.businessNameValidationMessage'),
                ]}
              />
              {Boolean(fieldsErrors?.name) && (
                <FormHelperText error={true}>{fieldsErrors?.name.join('.')}</FormHelperText>
              )}
            </div>
            <div>
              <FormControl sx={{ width: '100%' }}>
                <Autocomplete
                  id='country'
                  openOnFocus={true}
                  options={countriesOptions}
                  isOptionEqualToValue={(option, value) => option.title === value.title}
                  getOptionLabel={(option) => option.title}
                  value={country}
                  onChange={(
                    event: React.SyntheticEvent,
                    newValue: {
                      title: string;
                      value: string;
                    } | null
                  ) => {
                    setCountry(newValue);
                  }}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        {option.title}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='standard'
                      required={countriesOptions.length > 0}
                      label={t('labels.country')}
                      disabled={countriesOptions.length === 0}
                    />
                  )}
                  disabled={countriesOptions.length === 0}
                  sx={{ width: '100%' }}
                />
              </FormControl>
            </div>
            <div>
              <PhoneNumberInput
                value={businessPhoneNumber}
                onChange={(v) => {
                  setBusinessPhoneNumber(v);
                  setPhoneError(null);
                  handleInputsPossibleErrors('phone_number');
                }}
                disabled={false}
                label={t('formInputs.businessPhoneNumberInputLabel') + ' *'}
                error={Boolean(phoneError)}
                helperText={phoneError ?? undefined}
              />
              {Boolean(fieldsErrors?.phone_number) && (
                <FormHelperText error={true}>{fieldsErrors?.phone_number.join('.')}</FormHelperText>
              )}
            </div>
            <div>
              <TextValidator
                label={
                  <>
                    <Box component='span' sx={{ marginRight: 0.5 }}>
                      {t('labels.ABNorACN')}
                    </Box>
                    <Tooltip
                      title={t('labels.registrationNumberTypes')}
                      placement='top'
                      arrow={true}
                    >
                      <InfoIcon
                        fontSize='small'
                        color='secondary'
                        sx={{ flexShrink: 0 }}
                        className={'info-icon'}
                      />
                    </Tooltip>
                  </>
                }
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleBusinessABNorACNChange(e)}
                name='businessABNorACN'
                validators={['ABNorACNLength']}
                value={businessABNorACN}
                errorMessages={[t('messages.ABNandACNMessage')]}
              />
              {Boolean(fieldsErrors?.business_abn_acn) && (
                <FormHelperText error={true}>
                  {fieldsErrors?.business_abn_acn.join('.')}
                </FormHelperText>
              )}
            </div>
            <div>
              <WhatsAppOptInCheckbox
                value={whatsappOptIn}
                onChange={setWhatsappOptIn}
                disabled={false}
              />
            </div>
            <div className='buttons-row'>
              <Button
                id='companyCreateNextBtn'
                type='submit'
                variant='contained'
                className='btn-action-basic btn-default'
                disabled={!canSubmit || submitting}
              >
                {submitting ? t('buttons.submitting') : t('buttons.next')}
              </Button>
            </div>
          </ValidatorForm>
        </div>
      </div>
      {submitting && <ProgressOverlay />}
    </>
  );
};
